import { useCallback, useEffect, useState } from "react";
import { ApprovalRequest, ApprovalRequestSchema } from "../models/approval";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";

interface FetchRequestsFilters {
  status?: string;
  entityType?: string;
  limit?: number;
  offset?: number;
}

export const useApprovalRequests = ({
  fetchOnLoad = true,
  filters,
}: {
  fetchOnLoad?: boolean;
  filters?: FetchRequestsFilters;
}) => {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);

  const fetchRequests = useCallback(
    async (overrideFilters?: FetchRequestsFilters) => {
      setLoading(true);
      try {
        const queryFilters = overrideFilters ?? filters;
        const query: Record<string, string> = {};
        if (queryFilters?.status) query.status = queryFilters.status;
        if (queryFilters?.entityType)
          query.entity_type = queryFilters.entityType;
        if (queryFilters?.limit) query.limit = String(queryFilters.limit);
        if (queryFilters?.offset) query.offset = String(queryFilters.offset);

        const data = await fetchFastApi({
          path: "/approvals/requests",
          method: "GET",
          query,
        });
        if (data.requests) {
          const parsed = ApprovalRequestSchema.array().parse(data.requests);
          setRequests(parsed);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch approval requests");
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  const fetchRequest = useCallback(async (requestId: string) => {
    try {
      const data = await fetchFastApi({
        path: `/approvals/requests/${requestId}`,
        method: "GET",
      });
      return ApprovalRequestSchema.parse(data.request);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch approval request");
    }
  }, []);

  const approveRequest = useCallback(
    async (requestId: string, comment?: string) => {
      try {
        await fetchFastApi({
          path: `/approvals/requests/${requestId}/approve`,
          method: "POST",
          body: { comment: comment ?? null },
        });
        toast.success("Request approved");
        await fetchRequests();
      } catch (error) {
        console.error(error);
        toast.error("Failed to approve request");
      }
    },
    [fetchRequests],
  );

  const rejectRequest = useCallback(
    async (requestId: string, comment?: string) => {
      try {
        await fetchFastApi({
          path: `/approvals/requests/${requestId}/reject`,
          method: "POST",
          body: { comment: comment ?? null },
        });
        toast.success("Request rejected");
        await fetchRequests();
      } catch (error) {
        console.error(error);
        toast.error("Failed to reject request");
      }
    },
    [fetchRequests],
  );

  const cancelRequest = useCallback(
    async (requestId: string, comment?: string) => {
      try {
        await fetchFastApi({
          path: `/approvals/requests/${requestId}/cancel`,
          method: "POST",
          body: { comment: comment ?? null },
        });
        toast.success("Request cancelled");
        await fetchRequests();
      } catch (error) {
        console.error(error);
        toast.error("Failed to cancel request");
      }
    },
    [fetchRequests],
  );

  const applyRequest = useCallback(
    async (requestId: string) => {
      try {
        await fetchFastApi({
          path: `/approvals/requests/${requestId}/apply`,
          method: "POST",
        });
        toast.success("Request applied");
        await fetchRequests();
      } catch (error) {
        console.error(error);
        toast.error("Failed to apply request");
      }
    },
    [fetchRequests],
  );

  useEffect(() => {
    if (fetchOnLoad) {
      fetchRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnLoad]);

  return {
    loading,
    requests,
    fetchRequests,
    fetchRequest,
    approveRequest,
    rejectRequest,
    cancelRequest,
    applyRequest,
  };
};
